import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Heading,
  Input,
  SimpleGrid,
  FormControl,
  FormLabel,
  Select,
  Textarea,
  Checkbox,
  Flex,
  Link,
  Text,
  RadioGroup,
  HStack,
  Radio,
  InputLeftElement,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  setName,
  setStatus,
  setSex,
  setCity,
  setEmail,
  setPhone,
  setAdditionalDetails,
  setFileNumber,
  setExistingCustomer,
  setUserFileNumberBool,
  setFileNumberValidate,
  submitForm,
  resetForm,
} from "../reducers/bookingFormSlice";
import { setCurrentStep } from "../reducers/LocationSlice";
import Confirmation from "./Confirmation";
import { fetchFileNumberValidation, submitBookingApi } from "../api/api";
import { setBookingDetails } from "../reducers/KiidoBookingSlice";
import { FaCheck, FaTimes } from "react-icons/fa";

const BookingForm = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const [existingCustomerRadio, setExistingCustomerRadio] = useState('')

  const formRef = useRef(null); // Reference to form container
  const nameInputRef = useRef(null); // Reference to the first input field

  // Redux state
  const {
    name,
    status,
    sex,
    city,
    email,
    phone,
    additionalDetails,
    selectedTimeSlot,
    filenumber,
    userFileNumberBool,
    fileNumberValidate,
    existingCustomer
  } = useSelector((state) => state.bookingForm);

  const { bookingDetails } = useSelector((state) => state.booking);



  const { currentStep } = useSelector((state) => state.location);

  const [termsChecked, setTermsChecked] = useState(false);
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
  });
  const [isFileNumberValid, setIsFileNumberValid] = useState(false); // null: not validated, true: valid, false: invalid
  const [apiErrorMessage, setApiErrorMessage] = useState("");
  const [filenumbervalue, setFileNumberValue] = useState()
  const [loadingStateValidation, setLoadingStateValidation] = useState(false);
  const [validationResponse, setValidationResponse] = useState()

  const isSubmitDisabled =
    !name ||
    !email ||
    !phone ||
    errors.name ||
    errors.email ||
    errors.phone ||
    !termsChecked ||
    !selectedTimeSlot;

  // Scroll to the form and focus the first input field when this component renders
  useEffect(() => {
    formRef.current?.scrollIntoView({ behavior: "smooth" });

  }, []);


  useEffect(() => {
    // console.log("filenumber redux state ", bookingDetails.file_Number)
    setFileNumberValue(bookingDetails.file_Number)
    if (bookingDetails.file_Number) {
      setExistingCustomerRadio('1')
    }
  }, [bookingDetails.file_Number])

  // Validation functions
  const validateEmail = (email) =>
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
  const validatePhone = (phone) => /^(\+[0-9]{1,3}|0)[0-9]{9,10}$/.test(phone);

  // Form field handlers
  const handleNameChange = (e) => {
    const value = e.target.value;
    dispatch(setName(value));
    setErrors((prev) => ({
      ...prev,
      name: value.trim() ? "" : t("content.name_required"),
    }));
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    dispatch(setEmail(value));
    setErrors((prev) => ({
      ...prev,
      email: validateEmail(value) ? "" : t("content.email_invalid"),
    }));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    dispatch(setPhone(value));
    setErrors((prev) => ({
      ...prev,
      phone: validatePhone(value) ? "" : t("content.phone_number_invalid"),
    }));
  };

  const handleExistingCustomerChange = (value) => {

    // const value = e.target.value;
    dispatch(setExistingCustomer(value === "1"));
    setExistingCustomerRadio(value);
    // alert("handleExistingCustomerChange");
    // console.log("handleExistingCustomerChange", validationResponse);
    // setErrors((prev) => ({
    //   ...prev,
    //   existingCustomer: validatePhone(value) ? "" : t("content.phone_number_invalid"),
    // }));

    if (value === "2") {
      setApiErrorMessage('')
      setIsFileNumberValid(false)
      setErrors((prev) => ({
        ...prev,
        filenumber: ''
      }))
      dispatch(setFileNumber(''));
      setFileNumberValue('')
    }
  }

  const handleFileNumberChange = async (e) => {
    const value = e.target.value;
    setFileNumberValue(value);
    dispatch(setFileNumber(value));

    // Reset validation states if length is not 16
    if (value.length !== 16) {
      setErrors((prev) => ({
        ...prev,
        filenumber: value.length < 16
          ? t("content.error.file_number_too_short")
          : value.length > 16
            ? t("content.error.file_number_too_long")
            : "",
      }));

      setApiErrorMessage("");
      setIsFileNumberValid(false);
      return;
    }
    const slotNumber = selectedTimeSlot.tentative_availability_slot

    try {
      const response = await fetchFileNumberValidation({ value, slotNumber });
      // console.log("response", response);
      if (response) {
        setIsFileNumberValid(true);
        setApiErrorMessage("");
        setErrors((prev) => ({
          ...prev,
          filenumber: ''
        }))
        setValidationResponse(response.participants)
        dispatch(setUserFileNumberBool(response.user_file_number));
        const { name, email, phone, sex, city } = response.participants[0];
        // console.log("name, email, phone", name, email, phone, response.participants)
        dispatch(setName(name));
        dispatch(setEmail(email));
        dispatch(setPhone(phone));
        dispatch(setSex(sex));
        dispatch(setCity(city));
        dispatch(setFileNumberValidate(true));
      } else {
        setIsFileNumberValid(false);
        setApiErrorMessage(t("content.error.invalid_file_number"));
      }
    } catch (error) {
      console.log("error", error)
      dispatch(setUserFileNumberBool(false));
      setIsFileNumberValid(false);
      setApiErrorMessage(t("content.error.invalid_file_number"));
    }
  };




  const handleSubmit = async () => {
    if (isSubmitDisabled) return;

    // console.log("Form submitted");

    const bookingData = {
      date: selectedTimeSlot?.date || "",
      time: selectedTimeSlot?.time || "",
      service: selectedTimeSlot?.service || "",
      language: i18n.language,
      additional_info: additionalDetails,
      ...(existingCustomerRadio === "1" && { file_Number: filenumbervalue }),
      existingCustomer: existingCustomer,
      participants: [
        {
          name: name,
          status: status,
          sex: sex,
          city: city,
          email: email,
          phone: phone,
        },
      ].filter((participant) => participant.name.trim() !== ""),
    };

    dispatch(setBookingDetails(bookingData));
    dispatch(setCurrentStep(currentStep + 1)); // Move to the next step

    // try {
    //   const resData = await submitBookingApi(bookingData);
    //   console.log("Response Data:", resData);

    //   dispatch(submitForm());
    //   dispatch(setCurrentStep(currentStep + 1)); // Move to the next step
    // } catch (error) {
    //   console.error("Error:", error);
    //   dispatch(submitFormFailure(error.message));
    // }
  };

  return (
    <>
      <Box ref={formRef} p={6} bg="white" borderRadius="md" boxShadow="md">
        <Heading as="h3" size="lg" mb={4} mt={4} color="#FF6666">
          {t("content.personal_details")}
        </Heading>
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>

          <FormControl id="anexistingcustomer" isRequired>
            <FormLabel>{t("content.an_existing_customer")}</FormLabel>
            <RadioGroup value={existingCustomer ? "1" : "2"} onChange={handleExistingCustomerChange}>
              <HStack gap="6">
                <Radio
                  value="1"
                  _checked={{
                    bg: "#FF6666", // Fill color for the checked circle
                    borderColor: "#FF6666", // Outer border color for the checked state
                  }}
                  _hover={{
                    borderColor: "#FF6666", // Outer border color on hover
                  }}
                >
                  Yes
                </Radio>
                <Radio
                  value="2"
                  _checked={{
                    bg: "#FF6666",
                    borderColor: "#FF6666",
                  }}
                  _hover={{
                    borderColor: "#FF6666",
                  }}
                >
                  No
                </Radio>
              </HStack>
            </RadioGroup>
          </FormControl>


          <FormControl
            id="filenumber"
            isRequired={existingCustomerRadio === "1"} // Required if "Yes" is selected
            isInvalid={!!errors.filenumber}
          >
            <FormLabel>{t("content.confirmation.file_number")}</FormLabel>
            <InputGroup>
              <InputRightElement
                className="InputRight"
                pointerEvents="none"
                children={existingCustomerRadio === "1" ? (isFileNumberValid === true ? <FaCheck color="green" /> : <FaTimes color="red" />) : ''}
                size="xs"
              />
              <Input
                value={filenumbervalue}
                onChange={handleFileNumberChange}
                placeholder={t("content.File_number_characters")}
                bg="gray.100"
                isDisabled={existingCustomerRadio !== "1"} // Disable if "No" is selected
              />
            </InputGroup>
            {errors.filenumber && (
              <Text color="red.500" fontSize="sm">
                {errors.filenumber}
              </Text>
            )}
            {apiErrorMessage && (
              <Text color="red.500" fontSize="sm">
                {apiErrorMessage}
              </Text>
            )}
          </FormControl>

          <FormControl id="name" isRequired isInvalid={!!errors.name}>
            <FormLabel>{t("content.name")}</FormLabel>
            <Input
              ref={nameInputRef}
              value={name}
              onChange={handleNameChange}
              placeholder={t("content.name")}
              bg="gray.100"
            />
            {errors.name && (
              <Text color="red.500" fontSize="sm">
                {errors.name}
              </Text>
            )}
          </FormControl>

          <FormControl id="status">
            <FormLabel>{t("content.status")}</FormLabel>
            <Select
              value={status}
              onChange={(e) => dispatch(setStatus(e.target.value))}
              placeholder={t("content.status")}
              bg="gray.100"
            >
              <option value="mother">{t('content.mother')}</option>
              <option value="father">{t('content.father')}</option>
              <option value="brother">{t('content.brother')}</option>
              <option value="sister">{t('content.sister')}</option>
              <option value="grandmother">{t('content.grandmother')}</option>
              <option value="grandfather">{t('content.grandfather')}</option>
              <option value="grandparent">{t('content.grandparent')}</option>
              <option value="grandson">{t('content.grandson')}</option>
              <option value="granddaughter">{t('content.granddaughter')}</option>
              <option value="grandchild">{t('content.grandchild')}</option>
              <option value="uncle">{t('content.uncle')}</option>
              <option value="aunt">{t('content.aunt')}</option>
              <option value="neice">{t('content.neice')}</option>
              <option value="nephew">{t('content.nephew')}</option>
              <option value="cousin">{t('content.cousin')}</option>
              <option value="sister_in_law">{t('content.sister_in_law')}</option>
              <option value="brother_in_law">{t('content.brother_in_law')}</option>
              <option value="mother_in_law">{t('content.mother_in_law')}</option>
              <option value="father_in_law">{t('content.father_in_law')}</option>
              <option value="fiance">{t('content.fiance')}</option>
              <option value="friend">{t('content.friend')}</option>
            </Select>
          </FormControl>

          <FormControl id="sex">
            <FormLabel>{t("content.sex")}</FormLabel>
            <Select
              value={sex}
              onChange={(e) => dispatch(setSex(e.target.value))}
              placeholder={t("content.sex")}
              bg="gray.100"
            >
              <option value="M">{t("content.male")}</option>
              <option value="F">{t("content.female")}</option>
            </Select>
          </FormControl>

          <FormControl id="city">
            <FormLabel>{t("content.city")}</FormLabel>
            <Input
              value={city}
              onChange={(e) => dispatch(setCity(e.target.value))}
              placeholder={t("content.city")}
              bg="gray.100"
            />
          </FormControl>

          <FormControl id="email" isRequired isInvalid={!!errors.email}>
            <FormLabel>{t("content.email")}</FormLabel>
            <Input
              value={email}
              onChange={handleEmailChange}
              type="email"
              placeholder={t("content.email")}
              bg="gray.100"
            />
            {errors.email && (
              <Text color="red.500" fontSize="sm">
                {errors.email}
              </Text>
            )}
          </FormControl>

          <FormControl id="phone" isRequired isInvalid={!!errors.phone}>
            <FormLabel>{t("content.phone_number")}</FormLabel>
            <Input
              value={phone}
              onChange={handlePhoneChange}
              type="tel"
              placeholder={t("content.phone_number")}
              bg="gray.100"
            />
            {errors.phone && (
              <Text color="red.500" fontSize="sm">
                {errors.phone}
              </Text>
            )}
          </FormControl>
        </SimpleGrid>

        <FormControl id="additional-details" mt={4}>
          <FormLabel>{t("content.additional_details")}</FormLabel>
          <Textarea
            value={additionalDetails}
            onChange={(e) => dispatch(setAdditionalDetails(e.target.value))}
            placeholder={t("content.additional_details")}
            bg="gray.100"
          />
        </FormControl>

        <FormControl id="terms-of-use" mt={4}>
          <Flex alignItems="center">
            <Checkbox
              isChecked={termsChecked}
              onChange={(e) => setTermsChecked(e.target.checked)}
              size="lg"
            />
            <FormLabel mb={0} ml={2}>
              <Trans i18nKey="content.terms_of_use">
                <Link target="_blank" href={`https://kiido.fr/disclaimer?topic=privacy-policy&locale=${i18n.language}`}>
                  Privacy Policy
                </Link>
              </Trans>
            </FormLabel>
          </Flex>
        </FormControl>
      </Box>

      <Flex justifyContent="space-between" mt={4}>
        <Button onClick={() => dispatch(setCurrentStep(currentStep - 1))}
          bg="transparent"
          color="#FF6666"
          border="2px solid #FF6666"
          _hover={{ bg: "#FF6666", color: "white" }}
        >
          {t("content.pagination.previous")}
        </Button>
        <Button onClick={handleSubmit}
          bg="#FF6666"
          color="white"
          _hover={{ bg: "#E55353" }}
          isDisabled={
            isSubmitDisabled ||
            (existingCustomer === '1' && !userFileNumberBool)
          }
        >
          {t("content.pagination.next")}
        </Button>
      </Flex>

      <Confirmation isOpen={false} />
    </>
  );
};

export default BookingForm;
